
import { useState, useEffect, useRef } from 'react';

export default function AtelierSpecs() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const specs = [
    { label: '対応機種', value: 'iPad Pro 11" / 12.9"、iPad Air' },
    { label: '素材（外装）', value: 'プレミアムヴィーガンレザー' },
    { label: '素材（フレーム）', value: 'アルミニウム合金' },
    { label: '角度調整', value: '0°〜90°（無段階）' },
    { label: '重量', value: '約280g' },
    { label: 'Apple Pencil', value: '収納・充電対応' },
    { label: 'マグネット', value: '高強度ネオジム磁石' },
    { label: 'カラー', value: 'ブラック / グレー（予定）' },
  ];

  const compatibility = [
    { model: 'iPad Pro 11" (M4)', status: '対応' },
    { model: 'iPad Pro 12.9" (M2)', status: '対応' },
    { model: 'iPad Air (M2)', status: '対応' },
    { model: 'iPad Pro 11" (第3世代)', status: '対応' },
    { model: 'iPad Air (第5世代)', status: '対応' },
  ];

  return (
    <section
      ref={ref}
      className="py-20 sm:py-28 bg-[#0A0A0A] relative"
      aria-labelledby="af-specs-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="af-specs-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight"
          >
            スペック &amp; 対応モデル
          </h2>
          <p className="text-sm sm:text-base text-white/40 font-light">
            ※ 仕様は開発中のため変更になる場合があります
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10">
          {/* Specs Table */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h3 className="text-lg sm:text-xl font-medium text-white mb-5 flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-[#8E7AB5]/15 rounded-lg">
                <i
                  className="ri-file-list-3-line text-[#8E7AB5] text-sm"
                  aria-hidden="true"
                ></i>
              </div>
              製品仕様
            </h3>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
              <dl>
                {specs.map((spec, index) => (
                  <div
                    key={spec.label}
                    className={`flex justify-between items-center px-5 sm:px-6 py-4 ${
                      index !== specs.length - 1
                        ? 'border-b border-white/[0.05]'
                        : ''
                    }`}
                  >
                    <dt className="text-xs sm:text-sm text-white/40 font-light tracking-wide">
                      {spec.label}
                    </dt>
                    <dd className="text-xs sm:text-sm text-white/80 font-light tracking-wide text-right">
                      {spec.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Compatibility */}
          <div
            className={`transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h3 className="text-lg sm:text-xl font-medium text-white mb-5 flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-[#8E7AB5]/15 rounded-lg">
                <i
                  className="ri-tablet-line text-[#8E7AB5] text-sm"
                  aria-hidden="true"
                ></i>
              </div>
              対応モデル
            </h3>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden mb-6">
              {compatibility.map((item, index) => (
                <div
                  key={item.model}
                  className={`flex justify-between items-center px-5 sm:px-6 py-4 ${
                    index !== compatibility.length - 1
                      ? 'border-b border-white/[0.05]'
                      : ''
                  }`}
                >
                  <span className="text-xs sm:text-sm text-white/70 font-light">
                    {item.model}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-light">
                    <i
                      className="ri-checkbox-circle-fill text-sm"
                      aria-hidden="true"
                    ></i>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>

            {/* Price Preview */}
            <div className="bg-gradient-to-br from-[#8E7AB5]/10 to-[#8E7AB5]/5 border border-[#8E7AB5]/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#8E7AB5]/20 rounded-lg">
                  <i
                    className="ri-price-tag-3-line text-[#8E7AB5] text-sm"
                    aria-hidden="true"
                  ></i>
                </div>
                <h4 className="text-white font-medium text-sm">予定価格</h4>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl sm:text-4xl font-light text-white tracking-tight">
                  ¥12,800
                </span>
                <span className="text-sm text-white/40 font-light">
                  〜（税込）
                </span>
              </div>
              <p className="text-xs text-white/30 font-light leading-relaxed">
                ※ Makuake先行割引あり。LINE登録者には特別価格でご案内予定。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
